import React, { useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import { alpha, keyframes } from "@mui/material/styles";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ConfirmationNumberOutlinedIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

// Travels right to left across the word.
const labelShimmer = keyframes`
  0% { background-position: 200% center; }
  100% { background-position: 0% center; }
`;

// Matches the sparkle on the Rego AI panel header on the Checks page.
const aiSparkle = keyframes`
  0%, 100% {
    transform: scale(1) rotate(0deg);
    filter: drop-shadow(0 0 2px rgba(59,130,246,0.3));
  }
  50% {
    transform: scale(1.12) rotate(6deg);
    filter: drop-shadow(0 0 6px rgba(59,130,246,0.5));
  }
`;

/**
 * What Rego AI does, on one screen. Reached from the arrow on the evidence
 * panel and the dashboard strip. The pipeline diagram is the centrepiece:
 * clicking a stage swaps the detail panel beside it.
 */

const SOURCES = [
  { name: "Email", tools: "Gmail, Outlook", reads: "Whole thread + attachments", Icon: MailOutlineIcon },
  { name: "Tickets", tools: "Jira", reads: "Description, comments, fields", Icon: ConfirmationNumberOutlinedIcon },
  { name: "Drive", tools: "Drive, SharePoint", reads: "The file and its metadata", Icon: FolderOpenOutlinedIcon },
  { name: "Knowledge base", tools: "Confluence", reads: "Full page, reassembled", Icon: MenuBookOutlinedIcon },
];

const STAGES = [
  {
    id: "scans",
    n: "1",
    title: "Scans your sources",
    line: "Email, Drive, Jira and Confluence, whatever you have connected.",
    facts: [
      ["Reads", "Documents, threads, tickets and pages already in your tools"],
      ["Includes", "Attachments, and text inside them"],
      ["Never", "Asks you to upload or re-file anything"],
    ],
  },
  {
    id: "finds",
    n: "2",
    title: "Finds relevant evidence",
    line: "Picks out the records that actually prove a control.",
    facts: [
      ["Looks for", "The proof a check asks for, not just a keyword"],
      ["Example", "The record of a policy's approval, not only the policy"],
      ["Ignores", "Everything that has no bearing on a check"],
    ],
  },
  {
    id: "maps",
    n: "3",
    title: "Maps it to users and controls",
    line: "Attaches each piece of evidence to the right person and check.",
    facts: [
      ["Instead of", "Opening 100 profiles and uploading one by one"],
      ["Handles", "The mapping that used to be manual, at scale"],
      ["Result", "Evidence already filed where it belongs"],
    ],
  },
  {
    id: "surfaces",
    n: "4",
    title: "Surfaces it for review",
    line: "Shows you what it found and why, ready for your decision.",
    facts: [
      ["You see", "The evidence and the reasoning behind it"],
      ["You decide", "Approve or reject. Nothing is automatic"],
      ["Then", "Approved evidence counts towards the check"],
    ],
  },
];

const ENCRYPTION = [
  ["In transit", "Read over an authenticated, encrypted connection"],
  [
    "Application layer",
    "Email bodies, ticket descriptions and Confluence content are encrypted before they are written, so what reaches the database is already ciphertext",
  ],
  ["Database", "RDS encrypted with AWS KMS underneath that, so two independent layers"],
  ["Files", "Encrypted again before upload, then stored in S3 with SSE-KMS"],
];

const ISOLATION = [
  ["Database", "Each customer has their own, with no shared tables"],
  ["Keys", "Each customer has their own encryption keys"],
  ["Access", "Content is visible only to that integration's data owner"],
  ["Everyone else", "Sees counts and status, never names or content"],
];

const HowRegoAIWorks = () => {
  const theme = useTheme();
  const tc = theme.customColors;
  const info = tc.status.info;
  const [active, setActive] = useState("scans");

  const stage = STAGES.find((s) => s.id === active) || STAGES[0];
  const rule = `1px solid ${tc.border.subtle}`;
  const label = {
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.11em",
    textTransform: "uppercase",
    // secondary (4.83:1), not disabled (2.54:1) — these are real labels and
    // have to clear 4.5:1 at this size.
    color: tc.text.secondary,
  };
  const card = {
    border: rule,
    borderRadius: "12px",
    backgroundColor: tc.background.main,
  };
  const accentText = { color: info.main };
  // Same shimmer the dashboard strip runs on "Rego AI", so the product name
  // reads the same wherever it appears.
  const shimmerText = {
    background: `linear-gradient(90deg, ${tc.text.strong} 40%, ${info.main} 50%, ${tc.text.strong} 60%)`,
    backgroundSize: "200% auto",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    animation: `${labelShimmer} 3.5s linear infinite`,
    "@media (prefers-reduced-motion: reduce)": {
      animation: "none",
      WebkitTextFillColor: info.main,
    },
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        bgcolor: tc.background.header,
        backgroundImage:
          "radial-gradient(circle, rgba(3,27,137,0.06) 1px, transparent 1px)",
        backgroundSize: "22px 22px",
        py: { xs: 2.5, md: 3.5 },
      }}
    >
      {/* Fills the viewport with a generous cap, so a wide monitor does not
          leave the card stranded in the middle of the page. */}
      <Box
        sx={{
          maxWidth: 1600,
          mx: { xs: 2, md: 4, xl: "auto" },
          px: { xs: 2, md: 4 },
          borderRadius: "18px",
          border: rule,
          bgcolor: tc.background.main,
          boxShadow: "0 4px 24px rgba(3, 27, 137, 0.05)",
          py: { xs: 2.5, md: 4 },
        }}
      >
        {/* The promise floats right and the heading flows around it, so the
            title can run the full width instead of being boxed into a column. */}
        <Box sx={{ pt: { xs: 1, md: 2 }, mb: { xs: 3.5, md: 4 } }}>
          <Box
            sx={{
              ...card,
              borderColor: alpha(info.main, 0.3),
              backgroundColor: alpha(info.main, 0.05),
              px: 2.5,
              py: 2.25,
              float: { xs: "none", md: "right" },
              width: { xs: "100%", md: 330 },
              ml: { xs: 0, md: 4 },
              mb: { xs: 3, md: 2 },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.875, mb: 1.25 }}>
              <CheckCircleRoundedIcon sx={{ fontSize: 16, color: info.main }} />
              <Typography
                sx={{ fontSize: "13.5px", fontWeight: 650, color: tc.text.strong }}
              >
                Rego AI never approves anything
              </Typography>
            </Box>
            <Typography
              sx={{ fontSize: "12.5px", lineHeight: 1.7, color: tc.text.secondary }}
            >
              Every suggestion arrives pending. The data owner who already
              manages that source approves or rejects it, and only what they
              approve is ever attached to a control or reaches an auditor.
            </Typography>
          </Box>

          <Typography
            component="h1"
            sx={{
              fontSize: { xs: "26px", md: "clamp(30px, 3.2vw, 40px)" },
              fontWeight: 700,
              letterSpacing: "-0.03em",
              lineHeight: 1.14,
              color: tc.text.strong,
              mb: 2,
            }}
          >
            Your{" "}
            <Box component="span" sx={accentText}>
              compliance
            </Box>{" "}
            evidence, found for you by{" "}
            {/* Scales with the heading so it stays proportional, but sits a
                touch below cap height so it reads as a mark, not a letter. */}
            <Box component="span" sx={{ whiteSpace: "nowrap" }}>
              <Box component="span" sx={shimmerText}>
                Rego AI
              </Box>
              <AutoAwesomeIcon
                sx={{
                  fontSize: "0.62em",
                  color: info.main,
                  ml: 0.75,
                  verticalAlign: "0.06em",
                  animation: `${aiSparkle} 3s ease-in-out infinite`,
                  "@media (prefers-reduced-motion: reduce)": {
                    animation: "none",
                  },
                }}
              />
            </Box>
          </Typography>
          <Typography
            sx={{
              fontSize: "14.5px",
              lineHeight: 1.75,
              color: tc.text.secondary,
              maxWidth: 680,
            }}
          >
            Not asking you to upload documents. Not suggesting uploads. It goes
            out into the tools you already use, finds the data, and maps it
            correctly, so proving compliance stops being manual work.
          </Typography>

          {/* Clear the float so the section below starts on a fresh line. */}
          <Box sx={{ clear: "both" }} />
        </Box>

        {/* Pipeline: pick a stage, read its detail beside it */}
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            mb: 1.5,
          }}
        >
          <Typography sx={label}>How a suggestion is made</Typography>
          <Typography sx={{ fontSize: "11.5px", color: tc.text.secondary }}>
            Select a step for the detail
          </Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 2, md: 3 },
            mb: 4,
          }}
        >
          {/* The four stages as a connected run */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {STAGES.map((s, i) => {
              const on = s.id === active;
              return (
                <Box
                  key={s.id}
                  onClick={() => setActive(s.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setActive(s.id);
                    }
                  }}
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.75,
                    pl: 2,
                    pr: 1.75,
                    py: 1.5,
                    cursor: "pointer",
                    borderRadius: "10px",
                    backgroundColor: on ? alpha(info.main, 0.07) : "transparent",
                    transition: "background-color 160ms ease",
                    // Accent on the selected step, tying it to the panel beside it
                    boxShadow: on
                      ? `inset 3px 0 0 ${info.main}`
                      : "inset 3px 0 0 transparent",
                    "&:hover": {
                      backgroundColor: on
                        ? alpha(info.main, 0.09)
                        : tc.background.hover,
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${alpha(info.main, 0.5)}`,
                      outlineOffset: "-2px",
                    },
                    // Connector down to the next stage
                    "&::after":
                      i < STAGES.length - 1
                        ? {
                            content: '""',
                            position: "absolute",
                            left: 29,
                            top: 40,
                            bottom: -4,
                            width: "2px",
                            backgroundColor: alpha(info.main, 0.18),
                          }
                        : {},
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      zIndex: 1,
                      width: 22,
                      height: 22,
                      borderRadius: "50%",
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "11px",
                      fontWeight: 700,
                      backgroundColor: on ? info.main : tc.background.main,
                      color: on ? tc.button.text : tc.text.secondary,
                      border: on ? "none" : `1.5px solid ${tc.border.subtle}`,
                      transition: "all 160ms ease",
                    }}
                  >
                    {s.n}
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: on ? info.text : tc.text.strong,
                        lineHeight: 1.4,
                      }}
                    >
                      {s.title}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "12.5px",
                        lineHeight: 1.55,
                        color: tc.text.secondary,
                      }}
                    >
                      {s.line}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>

          {/* Detail for the selected stage */}
          {/* Stretches to the step list's height, and the fact rows share the
              slack, so the panel fills rather than floating in dead space. */}
          <Box
            sx={{
              ...card,
              p: 2.25,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1.75 }}>
              <Typography
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: alpha(info.main, 0.8),
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {stage.n}
              </Typography>
              <Typography
                sx={{ fontSize: "15px", fontWeight: 650, color: tc.text.strong }}
              >
                {stage.title}
              </Typography>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                justifyContent: "space-between",
              }}
            >
              {stage.facts.map(([k, v], i) => (
                <Box
                  key={k}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "116px 1fr" },
                    gap: { xs: 0.125, sm: 1.5 },
                    py: 1.25,
                    borderTop: i === 0 ? "none" : rule,
                  }}
                >
                  <Typography
                    sx={{ fontSize: "12px", fontWeight: 600, color: tc.text.secondary }}
                  >
                    {k}
                  </Typography>
                  <Typography sx={{ fontSize: "12.5px", color: tc.text.secondary }}>
                    {v}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* The concrete case, straight from how we describe it to customers */}
        <Box
          sx={{
            ...card,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 2, md: 0 },
            mb: 4,
            overflow: "hidden",
          }}
        >
          <Box sx={{ px: 2.25, py: 2 }}>
            <Typography sx={{ ...label, mb: 1 }}>Without it</Typography>
            <Typography
              sx={{ fontSize: "13px", lineHeight: 1.65, color: tc.text.secondary }}
            >
              You have 100 people, and a control asks for background checks or
              performance evaluations. Open each employee profile, upload
              documents one by one, repeat a few hundred times.
            </Typography>
          </Box>
          <Box
            sx={{
              px: 2.25,
              py: 2,
              backgroundColor: alpha(info.main, 0.04),
              borderLeft: { xs: "none", md: `1px solid ${tc.border.subtle}` },
              borderTop: { xs: `1px solid ${tc.border.subtle}`, md: "none" },
            }}
          >
            <Typography sx={{ ...label, color: info.text, mb: 1 }}>
              With Rego AI
            </Typography>
            <Typography
              sx={{ fontSize: "13px", lineHeight: 1.65, color: tc.text.secondary }}
            >
              If the data already exists, Rego AI goes, finds it, and maps it to
              the right employee and control automatically. It won't replace
              judgment. It removes the grunt work in front of it.
            </Typography>
          </Box>
        </Box>

        {/* Sources */}
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            mb: 1.5,
          }}
        >
          <Typography sx={label}>Where it looks</Typography>
          <Typography sx={{ fontSize: "11.5px", color: tc.text.secondary }}>
            Connect a shared account, not a personal one
          </Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
            gap: 1.5,
            mb: 1.5,
          }}
        >
          {SOURCES.map(({ name, tools, reads, Icon }) => (
            <Box key={name} sx={{ ...card, px: 1.75, py: 1.625 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.875 }}>
                <Icon sx={{ fontSize: 16, color: info.main }} />
                <Typography
                  sx={{ fontSize: "13.5px", fontWeight: 600, color: tc.text.strong }}
                >
                  {name}
                </Typography>
              </Box>
              <Typography
                sx={{ fontSize: "12.5px", color: tc.text.secondary, lineHeight: 1.5 }}
              >
                {reads}
              </Typography>
              <Typography sx={{ fontSize: "11px", color: tc.text.secondary, mt: 0.5 }}>
                {tools}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* The single most useful setup step, so it gets its own line. */}
        <Box
          sx={{
            ...card,
            backgroundColor: alpha(info.main, 0.04),
            borderColor: alpha(info.main, 0.22),
            px: 2.25,
            py: 1.75,
            mb: 4,
          }}
        >
          <Typography
            sx={{
              fontSize: "12.5px",
              fontWeight: 600,
              color: tc.text.strong,
              mb: 0.5,
            }}
          >
            Getting the most out of it
          </Typography>
          <Typography
            sx={{ fontSize: "12.5px", lineHeight: 1.7, color: tc.text.secondary }}
          >
            One Google connection covers both Gmail and Drive, and it is where
            most evidence lives. Connect a shared account such as{" "}
            <Box component="span" sx={{ fontWeight: 600, color: info.text }}>
              hr@yourcompany.com
            </Box>{" "}
            rather than an individual one, then add it as a CC on all of your
            people operations going forward. Background checks, performance
            evaluations and signed acknowledgements all become evidence Rego AI
            can find, and the records stay in one place when people change
            roles.
          </Typography>
        </Box>

        {/* Encryption + isolation, two columns */}
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
            mb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <LockOutlinedIcon sx={{ fontSize: 13, color: tc.text.disabled }} />
            <Typography sx={label}>Your data</Typography>
          </Box>
          <Typography sx={{ fontSize: "11.5px", color: tc.text.secondary }}>
            Encrypted at every layer, and kept apart per customer
          </Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: { xs: 2, md: 3 },
            mb: 3,
          }}
        >
          <Box sx={{ ...card, overflow: "hidden" }}>
            {ENCRYPTION.map(([stageName, what], i) => (
              <Box
                key={stageName}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "124px 1fr" },
                  gap: { xs: 0.125, sm: 2 },
                  px: 2,
                  py: 1.25,
                  borderTop: i === 0 ? "none" : rule,
                }}
              >
                <Typography
                  sx={{ fontSize: "12px", fontWeight: 600, color: info.text }}
                >
                  {stageName}
                </Typography>
                <Typography sx={{ fontSize: "12.5px", color: tc.text.secondary }}>
                  {what}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Same row shape as the encryption table beside it, so the two
              panels read as one section and share a height. */}
          <Box
            sx={{
              ...card,
              backgroundColor: tc.background.infoBox,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {ISOLATION.map(([k, v], i) => (
              <Box
                key={k}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "112px 1fr" },
                  gap: { xs: 0.125, sm: 2 },
                  px: 2,
                  py: 1.25,
                  flex: 1,
                  alignContent: "center",
                  borderTop: i === 0 ? "none" : rule,
                }}
              >
                <Typography
                  sx={{ fontSize: "12px", fontWeight: 600, color: info.text }}
                >
                  {k}
                </Typography>
                <Typography sx={{ fontSize: "12.5px", color: tc.text.secondary }}>
                  {v}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        <Typography
          sx={{ fontSize: "12px", color: tc.text.secondary, textAlign: "center" }}
        >
          Questions:{" "}
          <Box
            component="a"
            href="mailto:infra@solsphere.ai"
            sx={{
              color: info.text,
              textDecoration: "none",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            infra@solsphere.ai
          </Box>
        </Typography>
      </Box>
    </Box>
  );
};

export default HowRegoAIWorks;
